import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import practiceService from '../services/practiceService';
import api from '../services/api';
import { usePractice } from '../context/PracticeContext';
import { useAuth } from '../context/AuthContext';
import { TIMEZONE_OPTIONS, isValidPracticeTimezone } from '../utils/timezone';
import Tooltip from '../components/Tooltip';
import Templates from './Templates';
import { BookingConfigTab, DepartmentsTab, ProvidersTab } from './PracticeConfigPanel';
import DigitalFormsTab from '../components/DigitalFormsTab';
import PatientReportsConfig from '../components/PatientReportsConfig';
import PaymentSettings from './PaymentSettings';
import AppointmentTypeBrandOverridesTab from '../components/AppointmentTypeBrandOverridesTab';

// Map old tab keys to new merged tabs for backward compatibility
const TAB_ALIASES = {
  templates: 'communication',
  booking: 'ai',
  departments: 'resources',
  providers: 'resources',
  'chat-widget': 'online-presence',
  'landing-page': 'online-presence',
};

function PracticeSettings() {
  const { user, hasPermission } = useAuth();
  const { selectedPracticeId, loadPractices, selectedPractice } = usePractice();
  const [searchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') || 'general';
  const [activeTab, setActiveTab] = useState(TAB_ALIASES[rawTab] || rawTab);
  // The tab list below is filtered by permission, but the bodies render on
  // activeTab alone — so ?tab=payments put a clinical_admin straight into a
  // screen full of 403s that they were deliberately not shown a tab for.
  // Falling back to General keeps the URL from routing around the filter.
  const [practiceGroups, setPracticeGroups] = useState([]);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      api.get('/practice-groups').then(res => setPracticeGroups(res.data || [])).catch(() => {});
    }
  }, [user?.role]);

  if (!selectedPracticeId) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Practice Settings</h2>
        </div>
        <div style={styles.emptyState}>Please select a practice to configure settings.</div>
      </div>
    );
  }

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'communication', label: 'Communication' },
    { key: 'ai', label: 'AI & Automation' },
    { key: 'ehr', label: 'EHR Integration' },
    { key: 'resources', label: 'Practice Resources' },
    // commons #231: visible to anyone with intake:view; read-only unless intake:manage
    { key: 'digital-forms', label: 'Digital Forms', permission: 'intake:view' },
    { key: 'online-presence', label: 'Online Presence' },
    { key: 'payments', label: 'Payments', permission: 'billing:view' },
    { key: 'reports', label: 'Reports' },
    { key: 'brand-overrides', label: 'Brand Overrides' },
  ].filter(t => !t.permission || hasPermission(t.permission));

  const visibleTab = tabs.some(t => t.key === activeTab) ? activeTab : 'general';

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Practice Settings</h2>
        {selectedPractice && (
          <span style={styles.practiceBadge}>{selectedPractice.name}</span>
        )}
      </div>

      <div style={styles.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.tabActive : {})
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* commons #231: clinical_admin sees these tabs READ-ONLY (settings:update is
          admins-only). The wrapper disables every control inside and shows a banner,
          so each tab keeps its normal rendering. Communication → Templates stays
          editable for clinical_admin via notifications:manage. */}
      {visibleTab === 'general' && (
        <ReadOnlyGate readOnly={!hasPermission('settings:update')}>
          <GeneralTab
            practiceId={selectedPracticeId}
            practice={selectedPractice}
            practiceGroups={practiceGroups}
            user={user}
            onSaved={loadPractices}
          />
        </ReadOnlyGate>
      )}
      {visibleTab === 'communication' && (
        <CommunicationTab
          practiceId={selectedPracticeId}
          practice={selectedPractice}
          onSaved={loadPractices}
          senderReadOnly={!hasPermission('settings:update')}
          templatesReadOnly={!hasPermission('notifications:manage')}
        />
      )}
      {visibleTab === 'ai' && (
        <ReadOnlyGate readOnly={!hasPermission('settings:update')}><AiAutomationTab practiceId={selectedPracticeId} /></ReadOnlyGate>
      )}
      {visibleTab === 'ehr' && (
        <ReadOnlyGate readOnly={!hasPermission('settings:update')}><EHRSyncTab practiceId={selectedPracticeId} /></ReadOnlyGate>
      )}
      {visibleTab === 'resources' && (
        <ReadOnlyGate readOnly={!hasPermission('settings:update')}><PracticeResourcesTab practiceId={selectedPracticeId} /></ReadOnlyGate>
      )}
      {visibleTab === 'digital-forms' && (
        <DigitalFormsTab practiceId={selectedPracticeId} />
      )}
      {visibleTab === 'online-presence' && (
        <ReadOnlyGate readOnly={!hasPermission('settings:update')}><OnlinePresenceTab practiceId={selectedPracticeId} /></ReadOnlyGate>
      )}
      {visibleTab === 'payments' && (
        <PaymentSettings practiceId={selectedPracticeId} />
      )}
      {visibleTab === 'reports' && (
        <ReadOnlyGate readOnly={!hasPermission('settings:update')}><PatientReportsConfig practiceId={selectedPracticeId} /></ReadOnlyGate>
      )}
      {visibleTab === 'brand-overrides' && (
        <ReadOnlyGate readOnly={!hasPermission('settings:update')}><AppointmentTypeBrandOverridesTab practiceId={selectedPracticeId} /></ReadOnlyGate>
      )}
    </div>
  );
}

/**
 * commons #231 — wraps a settings tab for roles that may VIEW but not CHANGE it.
 * `<fieldset disabled>` disables every input/select/textarea/button inside
 * without touching the tab components; the banner says who can change it.
 */
export function ReadOnlyGate({ readOnly, children }) {
  if (!readOnly) return children;
  return (
    <div>
      <div style={{ padding: '8px 12px', marginBottom: 12, background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6, color: '#78350f', fontSize: 13 }}>
        👁 View only — ask a practice admin to change these settings.
      </div>
      <fieldset disabled style={{ border: 'none', padding: 0, margin: 0, minWidth: 0, opacity: 0.85 }} aria-readonly="true">
        {children}
      </fieldset>
    </div>
  );
}

// ─── Merged Tab Wrappers ──────────────────────────────────────

function SubTabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb', paddingBottom: 8 }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          style={{
            padding: '6px 14px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6,
            background: active === t.key ? '#4f46e5' : '#fff',
            color: active === t.key ? '#fff' : '#374151',
            borderColor: active === t.key ? '#4f46e5' : '#d1d5db',
            cursor: 'pointer', fontWeight: 500,
          }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function CommunicationTab({ practiceId, practice, onSaved, senderReadOnly = false, templatesReadOnly = false }) {
  const [sub, setSub] = useState('messaging');
  return (
    <div>
      <SubTabBar tabs={[{ key: 'messaging', label: 'Sender Config' }, { key: 'templates', label: 'Templates' }]} active={sub} onChange={setSub} />
      {sub === 'messaging' && <ReadOnlyGate readOnly={senderReadOnly}><MessagingTab practiceId={practiceId} practice={practice} onSaved={onSaved} /></ReadOnlyGate>}
      {sub === 'templates' && <ReadOnlyGate readOnly={templatesReadOnly}><Templates /></ReadOnlyGate>}
    </div>
  );
}

function AiAutomationTab({ practiceId }) {
  const [sub, setSub] = useState('ai');
  return (
    <div>
      <SubTabBar tabs={[{ key: 'ai', label: 'AI Settings' }, { key: 'booking', label: 'Booking Config' }]} active={sub} onChange={setSub} />
      {sub === 'ai' && <AiTab practiceId={practiceId} />}
      {sub === 'booking' && <BookingConfigTab practiceId={practiceId} />}
    </div>
  );
}

function PracticeResourcesTab({ practiceId }) {
  const [sub, setSub] = useState('departments');
  return (
    <div>
      <SubTabBar tabs={[{ key: 'departments', label: 'Departments' }, { key: 'providers', label: 'Providers' }]} active={sub} onChange={setSub} />
      {sub === 'departments' && <DepartmentsTab practiceId={practiceId} />}
      {sub === 'providers' && <ProvidersTab practiceId={practiceId} />}
    </div>
  );
}

function OnlinePresenceTab({ practiceId }) {
  const [sub, setSub] = useState('chat-widget');
  return (
    <div>
      <SubTabBar tabs={[{ key: 'chat-widget', label: 'Chat Widget' }, { key: 'landing-page', label: 'Landing Page' }]} active={sub} onChange={setSub} />
      {sub === 'chat-widget' && <ChatWidgetTab practiceId={practiceId} />}
      {sub === 'landing-page' && <LandingPageTab practiceId={practiceId} />}
    </div>
  );
}

// ─── General Tab (from Practices.js) ───────────────────────────

function GeneralTab({ practiceId, practice, practiceGroups, user, onSaved }) {
  const [formData, setFormData] = useState({
    name: '', address: '', phone: '', email: '',
    booking_url: '', revenue_per_visit: 150,
    timezone: 'America/New_York', practice_group_id: '',
    nh_practice_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    if (practice) {
      setFormData({
        name: practice.name || '',
        address: practice.address || '',
        phone: practice.phone || '',
        email: practice.email || '',
        booking_url: practice.booking_url || '',
        revenue_per_visit: practice.revenue_per_visit || 150,
        timezone: practice.timezone || 'America/New_York',
        practice_group_id: practice.practice_group_id || '',
        nh_practice_id: practice.nh_practice_id || ''
      });
    }
  }, [practice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.name.trim()) {
      setError('Practice name is required');
      return;
    }

    try {
      setSaving(true);
      await practiceService.update(practiceId, {
        ...formData,
        practice_group_id: formData.practice_group_id ? parseInt(formData.practice_group_id) : null,
        revenue_per_visit: parseFloat(formData.revenue_per_visit) || 150,
      });
      await onSaved();
      setSuccess('Practice info updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSuccess(null);
  };

  const activeGroups = practiceGroups.filter(g => g.is_active);

  return (
    <>
    <form onSubmit={handleSubmit}>
      {error && <div style={styles.errorMsg}>{error}</div>}
      {success && <div style={styles.successMsg}>{success}</div>}

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Practice Information</h3>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Practice Name *</label>
            <input style={styles.input} type="text" name="name" value={formData.name} onChange={handleChange} required />
          </div>
          {user?.role === 'super_admin' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Practice Group</label>
              <select style={styles.input} name="practice_group_id" value={formData.practice_group_id} onChange={handleChange}>
                <option value="">Select Practice Group</option>
                {activeGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Address</label>
          <input style={styles.input} type="text" name="address" value={formData.address} onChange={handleChange} />
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Practice Contact Phone</label>
            <input style={styles.input} type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" />
            <span style={styles.helpText}>Main phone number shown to patients. Format: (xxx) xxx-xxxx</span>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input style={styles.input} type="email" name="email" value={formData.email} onChange={handleChange} />
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Booking URL <Tooltip text="Link to your online appointment booking system" /></label>
          <input style={styles.input} type="url" name="booking_url" value={formData.booking_url} onChange={handleChange} />
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Revenue Per Visit ($) <Tooltip text="Average revenue per patient visit" /></label>
            <input style={styles.input} type="number" name="revenue_per_visit" value={formData.revenue_per_visit} onChange={handleChange} min="0" step="0.01" />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Timezone <Tooltip text="Drives every reminder/SMS/voice AI/dayof_time schedule. Must be a real IANA tz — never UTC." /></label>
            <select style={styles.input} name="timezone" value={formData.timezone} onChange={handleChange}>
              {TIMEZONE_OPTIONS.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            {!isValidPracticeTimezone(formData.timezone) && (
              <div style={{
                marginTop: 8, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5',
                borderRadius: 6, color: '#991b1b', fontSize: 13
              }}>
                <strong>⚠ Practice timezone is missing or invalid.</strong> Notifications,
                reminders, voice AI prompts, and waitlist SMS will fire at wrong wall-clock
                times until you pick a real timezone above and save. (GH-92)
              </div>
            )}
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>NH Practice ID</label>
          <input
            style={{ ...styles.input, ...(practice?.nh_practice_id ? { background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' } : {}) }}
            type="text" name="nh_practice_id" value={formData.nh_practice_id}
            onChange={handleChange}
            placeholder="Newton Health internal ID"
            readOnly={!!practice?.nh_practice_id}
          />
          {practice?.nh_practice_id && (
            <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Locked after initial configuration. Contact super admin to change.</span>
          )}
        </div>
      </div>

      {/* Practice Logo */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Practice Logo</h3>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Upload your practice logo. Use {'{{practice_logo}}'} in email templates to display the logo image, or {'{{practice_logo_url}}'} for the raw URL.</p>
        {practice?.logo_url && logoUrl && (
          <div style={{ marginBottom: 12 }}>
            <img src={logoUrl} alt="Practice logo" style={{ maxHeight: 80, maxWidth: 240, borderRadius: 6, border: '1px solid #e5e7eb' }} />
          </div>
        )}
        {practice?.logo_url && !logoUrl && (
          <button type="button" style={styles.secondaryButton} onClick={async () => {
            try {
              const res = await api.request(`/practices/${practiceId}/branding/logo`);
              if (res.data?.url) setLogoUrl(res.data.url);
            } catch { /* ignore */ }
          }}>Show Current Logo</button>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input type="file" accept="image/*" id="logo-upload" style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setLogoUploading(true);
              try {
                const formData = new FormData();
                formData.append('logo', file);
                const res = await fetch(`/api/practices/${practiceId}/branding/logo`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('nh_auth_token') || localStorage.getItem('nh_context_token')}` },
                  body: formData,
                });
                const data = await res.json();
                if (data.success) {
                  setLogoUrl(data.data.url);
                  setSuccess('Logo uploaded successfully');
                  await onSaved();
                } else {
                  setError(data.error || 'Upload failed');
                }
              } catch (err) {
                setError(err.message);
              }
              setLogoUploading(false);
              e.target.value = '';
            }}
          />
          <label htmlFor="logo-upload" style={{ ...styles.secondaryButton, cursor: logoUploading ? 'not-allowed' : 'pointer', opacity: logoUploading ? 0.6 : 1 }}>
            {logoUploading ? 'Uploading...' : practice?.logo_url ? 'Replace Logo' : 'Upload Logo'}
          </label>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>PNG, JPG, or SVG. Max 2MB.</span>
        </div>
      </div>

      <div style={styles.actions}>
        <button type="submit" disabled={saving} style={styles.saveButton}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>

    {(user?.role === 'super_admin' || user?.user?.role === 'super_admin' || user?.isSuperAdmin) && (
      <TestModeSection practiceId={practiceId} />
    )}
    </>
  );
}

// ─── Test Mode Section (superadmin only) ──────────────────────────

function TestModeSection({ practiceId }) {
  const [enabled, setEnabled] = useState(false);
  const [allowlist, setAllowlist] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!practiceId) return;
    setLoading(true);
    api.getTestMode(practiceId).then(res => {
      if (res.success) {
        setEnabled(res.data.enabled);
        setAllowlist(res.data.allowlist || []);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [practiceId]);

  const isValidEntry = (val) => {
    const phone = /^\+1\d{10}$/.test(val);
    const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    return phone || email;
  };

  const addChip = () => {
    const val = inputValue.trim();
    if (!val) return;
    if (!isValidEntry(val)) {
      setInputError('Enter a valid phone (+1XXXXXXXXXX) or email');
      return;
    }
    if (allowlist.includes(val)) {
      setInputError('Already in list');
      return;
    }
    setAllowlist([...allowlist, val]);
    setInputValue('');
    setInputError('');
    setSaved(false);
  };

  const removeChip = (idx) => {
    setAllowlist(allowlist.filter((_, i) => i !== idx));
    setSaved(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addChip(); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateTestMode(practiceId, { enabled, allowlist });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save test mode:', err);
    }
    setSaving(false);
  };

  return (
    <div style={{ marginTop: 32, padding: 24, border: enabled ? '2px solid #f59e0b' : '1px solid #e5e7eb', borderRadius: 12, background: enabled ? '#fffbeb' : '#fff', opacity: loading ? 0.5 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: enabled ? '#92400e' : '#374151' }}>
            Test Mode
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
            When enabled, outbound SMS/email/voice is blocked for all contacts except those in the allowlist.
          </p>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }}
            style={{ width: 20, height: 20, accentColor: '#f59e0b' }}
          />
          <span style={{ fontSize: 14, fontWeight: 600, color: enabled ? '#92400e' : '#6b7280' }}>
            {enabled ? 'ON' : 'OFF'}
          </span>
        </label>
      </div>

      {enabled && (
        <>
          {enabled && (
            <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
              Test mode is ON — only allowlisted contacts will receive messages from this practice.
            </div>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Allowlisted Contacts
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {allowlist.map((item, idx) => (
                <span key={idx} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                  background: item.includes('@') ? '#dbeafe' : '#dcfce7',
                  color: item.includes('@') ? '#1e40af' : '#166534',
                  borderRadius: 16, fontSize: 13, fontWeight: 500
                }}>
                  {item.includes('@') ? '\u2709' : '\u260E'} {item}
                  <button onClick={() => removeChip(idx)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
                    color: '#9ca3af', padding: 0, lineHeight: 1
                  }}>&times;</button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); setInputError(''); }}
                onKeyDown={handleKeyDown}
                placeholder="+15551234567 or name@email.com"
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
              />
              <button onClick={addChip} style={{
                padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none',
                borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer'
              }}>Add</button>
            </div>
            {inputError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{inputError}</div>}
          </div>
        </>
      )}

      <button onClick={handleSave} disabled={saving} style={{
        padding: '10px 20px', background: saved ? '#10b981' : '#2563eb', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: saving ? 'wait' : 'pointer'
      }}>
        {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Test Mode Settings'}
      </button>
    </div>
  );
}

// ─── Messaging Tab (from Settings.js) ──────────────────────────

function MessagingTab({ practiceId, practice, onSaved }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [senderConfig, setSenderConfig] = useState({
    sms_sender_phone: '',
    email_sender_address: '',
    email_sender_name: ''
  });
  const [voicePhones, setVoicePhones] = useState({
    voice_phone_number: '',
    voice_inbound_phone: ''
  });
  const [emailVerification, setEmailVerification] = useState(null);

  useEffect(() => {
    loadAllConfig();
  }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const [senderRes, voiceRes] = await Promise.all([
        practiceService.getSenderConfig(practiceId),
        api.getVoiceConfig(practiceId).catch(() => ({ success: false }))
      ]);
      if (senderRes.data) {
        setSenderConfig({
          sms_sender_phone: senderRes.data.sms_sender_phone || '',
          email_sender_address: senderRes.data.email_sender_address || '',
          email_sender_name: senderRes.data.email_sender_name || ''
        });
      }
      if (voiceRes.success && voiceRes.data) {
        setVoicePhones({
          voice_phone_number: voiceRes.data.voice_phone_number || '',
          voice_inbound_phone: voiceRes.data.voice_inbound_phone || ''
        });
      }
    } catch (err) {
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSenderChange = (e) => {
    const { name, value } = e.target;
    setSenderConfig(prev => ({ ...prev, [name]: value }));
    setSuccess(null);
    if (name === 'email_sender_address') setEmailVerification(null);
  };

  const handleVerifyEmail = async () => {
    const email = senderConfig.email_sender_address;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address first');
      return;
    }
    setEmailVerification({ checking: true });
    setError(null);
    try {
      const result = await api.post(`/practices/${practiceId}/verify-email`, { email });
      if (result.success) {
        setEmailVerification({ ...result.data, checking: false });
      } else {
        setEmailVerification({ verified: false, message: result.error || 'Verification check failed', checking: false });
      }
    } catch (err) {
      setEmailVerification({ verified: false, message: err.message || 'Failed to check', checking: false });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (senderConfig.email_sender_address && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderConfig.email_sender_address)) {
      setError('Please enter a valid email address');
      return;
    }

    if (senderConfig.email_sender_address) {
      try {
        const check = await api.get(`/practices/${practiceId}/check-email?email=${encodeURIComponent(senderConfig.email_sender_address)}`);
        if (check.success && !check.data.available) {
          setError(`Email ${senderConfig.email_sender_address} is already used by "${check.data.conflict.practice_name}"`);
          return;
        }
      } catch (err) {
        // Non-blocking — server-side will also validate
      }
    }

    try {
      setSaving(true);
      // Only email sender fields are editable from this tab.
      // Phone assignments live in Phone Management (super_admin) — see banner above.
      await practiceService.updateSenderConfig(practiceId, {
        email_sender_address: senderConfig.email_sender_address,
        email_sender_name: senderConfig.email_sender_name,
      });
      setSuccess('Messaging configuration updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;

  return (
    <form onSubmit={handleSubmit}>
      {error && <div style={styles.errorMsg}>{error}</div>}
      {success && <div style={styles.successMsg}>{success}</div>}

      <div style={styles.infoBox}>
        <strong>Phone Number Formats:</strong> All system phone numbers use E.164 format (<code>+1XXXXXXXXXX</code>).
        The practice contact phone in General settings uses display format <code>(xxx) xxx-xxxx</code>.
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Phone Numbers</h3>

        <div style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: '#374151' }}>
          Phone numbers are managed centrally in Phone Management to enforce cross-practice uniqueness + audit trail.
          {user?.role === 'super_admin' && (
            <> <a href="/phone-management" style={{ color: '#2563EB', fontWeight: 600 }}>Open Phone Management →</a></>
          )}
          {user?.role !== 'super_admin' && (
            <> Contact your super-admin to change assignments.</>
          )}
        </div>

        <div style={phoneGroupStyles.group}>
          <div style={phoneGroupStyles.groupLabel}>SMS / Secure Messaging</div>
          <div style={styles.formGroup}>
            <label style={styles.label}>SMS Sender Phone <Tooltip text="10DLC or toll-free number registered in AWS Pinpoint for sending SMS to patients" /></label>
            <div style={{ ...styles.input, background: '#F9FAFB', color: '#111827', display: 'flex', alignItems: 'center' }}>
              {senderConfig.sms_sender_phone || <span style={{ color: '#9CA3AF' }}>— not assigned —</span>}
            </div>
          </div>
        </div>

        <div style={phoneGroupStyles.group}>
          <div style={phoneGroupStyles.groupLabel}>Voice AI - Outbound</div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Voice Outbound Phone <Tooltip text="Phone number used for outbound AI calls to patients" /></label>
            <div style={{ ...styles.input, background: '#F9FAFB', color: '#111827', display: 'flex', alignItems: 'center' }}>
              {voicePhones.voice_phone_number || <span style={{ color: '#9CA3AF' }}>— not assigned —</span>}
            </div>
          </div>
        </div>

        <div style={phoneGroupStyles.group}>
          <div style={phoneGroupStyles.groupLabel}>Voice AI - Inbound</div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Voice Inbound Phone <Tooltip text="Phone number patients call to reach the AI receptionist" /></label>
            <div style={{ ...styles.input, background: '#F9FAFB', color: '#111827', display: 'flex', alignItems: 'center' }}>
              {voicePhones.voice_inbound_phone || <span style={{ color: '#9CA3AF' }}>— not assigned —</span>}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Email Configuration</h3>
        <div style={styles.formGroup}>
          <label style={styles.label}>Sender Email Address</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input style={{ ...styles.input, flex: 1, ...(emailVerification && !emailVerification.checking ? (emailVerification.verified ? { border: '2px solid #22c55e' } : { border: '2px solid #ef4444' }) : {}) }} type="email" name="email_sender_address" value={senderConfig.email_sender_address} onChange={handleSenderChange} placeholder="noreply@yourpractice.com" />
            <button type="button" onClick={handleVerifyEmail} disabled={emailVerification?.checking || !senderConfig.email_sender_address} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', height: 38, ...(emailVerification?.checking ? { opacity: 0.7, cursor: 'not-allowed' } : {}) }}>
              {emailVerification?.checking ? 'Checking...' : 'Verify'}
            </button>
          </div>
          {emailVerification && !emailVerification.checking && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 6, fontSize: 13, border: '1px solid', marginTop: 8, lineHeight: 1.4, backgroundColor: emailVerification.verified ? '#f0fdf4' : '#fef2f2', borderColor: emailVerification.verified ? '#bbf7d0' : '#fecaca', color: emailVerification.verified ? '#166534' : '#991b1b' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={emailVerification.verified ? '#22c55e' : '#ef4444'} strokeWidth="2.5" style={{ flexShrink: 0 }}>
                {emailVerification.verified ? <path d="M20 6L9 17l-5-5" /> : <><path d="M18 6L6 18" /><path d="M6 6l12 12" /></>}
              </svg>
              <span>{emailVerification.message}</span>
            </div>
          )}
          <span style={styles.helpText}>Must be verified in AWS SES. Click "Verify" to check status in real time.</span>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Sender Display Name</label>
          <input style={styles.input} type="text" name="email_sender_name" value={senderConfig.email_sender_name} onChange={handleSenderChange} placeholder="Your Practice Name" />
          <span style={styles.helpText}>Name shown in the "From" field of emails.</span>
        </div>
      </div>

      <div style={styles.infoBox}>
        <strong>Note:</strong> If not configured, the system uses default sender settings from the environment configuration.
      </div>

      <div style={styles.actions}>
        <button type="submit" disabled={saving} style={styles.saveButton}>
          {saving ? 'Saving...' : 'Save Messaging Settings'}
        </button>
        <button type="button" onClick={loadAllConfig} disabled={saving} style={styles.secondaryButton}>
          Reset
        </button>
      </div>
    </form>
  );
}

const phoneGroupStyles = {
  group: {
    padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '6px',
    marginBottom: '12px', borderLeft: '3px solid #4f46e5'
  },
  groupLabel: {
    fontSize: '12px', fontWeight: '600', color: '#4f46e5',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px'
  }
};

// ─── AI Tab (from AiConfig.js) ─────────────────────────────────

function AiTab({ practiceId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadConfig();
  }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfig = async () => {
    try {
      const result = await api.getAiConfig(practiceId);
      if (result.success) setConfig(result.data);
    } catch (err) {
      console.error('Load AI config error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const result = await api.updateAiConfig(practiceId, config);
      if (result.success) {
        setConfig(result.data);
        setMessage({ type: 'success', text: 'AI settings saved successfully.' });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to save.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const updateTemplate = (key, value) => {
    setConfig(prev => ({
      ...prev,
      ai_response_templates: { ...(prev.ai_response_templates || {}), [key]: value }
    }));
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;
  if (!config) return <div style={styles.loading}>No AI configuration found.</div>;

  const templates = config.ai_response_templates || {};

  return (
    <div>
      {message && (
        <div style={{
          ...styles.messageBanner,
          background: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color: message.type === 'success' ? '#166534' : '#dc2626'
        }}>
          {message.text}
        </div>
      )}

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>General</h3>
        <div style={styles.formGroup}>
          <label style={styles.label}>
            <input type="checkbox" checked={config.ai_sms_enabled === 1} onChange={(e) => updateField('ai_sms_enabled', e.target.checked ? 1 : 0)} style={styles.checkbox} />
            Enable AI SMS Scheduling
          </label>
          <span style={styles.helpText}>AI automatically detects scheduling intent and sends booking links.</span>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Booking Link Template</label>
          <input style={styles.input} type="text" value={config.booking_link_template || ''} onChange={(e) => updateField('booking_link_template', e.target.value)} placeholder="https://booking.example.com/schedule?practice={practice_id}" />
          <span style={styles.helpText}>Use {'{practice_id}'} as a placeholder.</span>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Confidence Threshold</label>
          <div style={styles.sliderRow}>
            <input type="range" min="0.5" max="0.95" step="0.05" value={config.confidence_threshold || 0.70} onChange={(e) => updateField('confidence_threshold', parseFloat(e.target.value))} style={styles.slider} />
            <span style={styles.sliderValue}>{((config.confidence_threshold || 0.70) * 100).toFixed(0)}%</span>
          </div>
          <span style={styles.helpText}>AI escalates to humans when confidence is below this threshold.</span>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Notifications</h3>
        <div style={styles.formGroup}>
          <label style={styles.label}>
            <input type="checkbox" checked={config.escalation_notifications_enabled === 1} onChange={(e) => updateField('escalation_notifications_enabled', e.target.checked ? 1 : 0)} style={styles.checkbox} />
            Enable escalation notifications
          </label>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Notification Email</label>
          <input style={styles.input} type="email" value={config.notification_email || ''} onChange={(e) => updateField('notification_email', e.target.value)} placeholder="admin@practice.com" />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Notification SMS Number</label>
          <input style={styles.input} type="tel" value={config.notification_sms || ''} onChange={(e) => updateField('notification_sms', e.target.value)} placeholder="+15551234567" />
        </div>

        <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.75rem' }}>
            <input type="checkbox" checked={config.notification_quiet_hours_enabled === 1} onChange={(e) => updateField('notification_quiet_hours_enabled', e.target.checked ? 1 : 0)} style={styles.checkbox} />
            Enable Quiet Hours (defer reminders during off-hours)
          </label>
          {config.notification_quiet_hours_enabled === 1 && (
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#6b7280' }}>Start (no send after)</label>
                <input type="time" value={config.notification_quiet_hours_start || '21:00'} onChange={(e) => updateField('notification_quiet_hours_start', e.target.value)} style={{ ...styles.input, width: '130px' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#6b7280' }}>End (resume sending at)</label>
                <input type="time" value={config.notification_quiet_hours_end || '08:00'} onChange={(e) => updateField('notification_quiet_hours_end', e.target.value)} style={{ ...styles.input, width: '130px' }} />
              </div>
            </div>
          )}
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>Appointment confirmations, cancellations, and reschedule notices are always sent immediately regardless of quiet hours.</span>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Response Templates</h3>
        <div style={styles.formGroup}>
          <label style={styles.label}>Booking Link Message</label>
          <textarea style={styles.textarea} value={templates.booking_link || ''} onChange={(e) => updateTemplate('booking_link', e.target.value)} placeholder="You can book your appointment here: {link}" rows={2} />
          <span style={styles.helpText}>Use {'{link}'} where the booking URL should appear.</span>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Escalation Message</label>
          <textarea style={styles.textarea} value={templates.escalation || ''} onChange={(e) => updateTemplate('escalation', e.target.value)} placeholder="I'm connecting you with our team to help you with that." rows={2} />
          <span style={styles.helpText}>Sent when the assistant hands a conversation to your team. Staff do read replies in the messaging inbox, so avoid telling the patient that replies are not monitored.</span>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Unclear Request Message</label>
          <textarea style={styles.textarea} value={templates.unclear || ''} onChange={(e) => updateTemplate('unclear', e.target.value)} placeholder="Could you clarify what you need?" rows={2} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Possible Emergency Message</label>
          <textarea style={styles.textarea} value={templates.emergency || ''} onChange={(e) => updateTemplate('emergency', e.target.value)} placeholder="If this is a medical emergency, call 911 or go to your nearest emergency room now. Our team will also see your message." rows={3} />
          <span style={styles.helpText}>Sent instead of the escalation message, at any hour, when a patient's text contains emergency language such as chest pain, trouble breathing or self-harm. Leave blank to use the wording above.</span>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>After-Hours Message</label>
          <textarea style={styles.textarea} value={templates.after_hours || ''} onChange={(e) => updateTemplate('after_hours', e.target.value)} placeholder="Leave blank to append your opening time to the escalation message automatically." rows={3} />
          <span style={styles.helpText}>Used instead of the escalation message when a conversation is handed over outside your sending window, so the patient is told when they will actually hear back rather than being left waiting overnight.</span>
        </div>
      </div>

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Answer practical questions</h3>
        <div style={styles.formGroup}>
          <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={templates.faq_enabled === true || templates.faq_enabled === 'on' || templates.faq_enabled === 1}
              onChange={(e) => updateTemplate('faq_enabled', e.target.checked)}
            />
            Let the assistant answer practical questions automatically
          </label>
          <span style={styles.helpText}>
            Off by default. When on, the assistant answers practical questions such as hours, address, parking and phone
            using only the facts below plus your practice record. Anything the facts do not cover still goes to your team.
            Clinical questions, refills, results, billing and complaints always go to a person regardless of this setting.
          </span>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Practice facts the assistant may use</label>
          <textarea
            style={styles.textarea}
            value={templates.faq_facts || ''}
            onChange={(e) => updateTemplate('faq_facts', e.target.value)}
            placeholder={'One fact per line, for example:\nFree parking is available behind the building.\nWe are closed for lunch 12-1pm.\nBring your insurance card and photo ID to your first visit.'}
            rows={6}
          />
          <span style={styles.helpText}>One fact per line. The assistant may use only these facts plus your practice name, address, phone and hours, and is told to refuse anything else rather than guess.</span>
        </div>
      </div>

      <div style={styles.actions}>
        <button onClick={handleSave} disabled={saving} style={styles.saveButton}>
          {saving ? 'Saving...' : 'Save AI Settings'}
        </button>
      </div>
    </div>
  );
}

// ─── EHR Sync Tab ───────────────────────────────────────────────

function EHRSyncTab({ practiceId }) {
  const { selectedPractice } = usePractice();
  const [syncStatus, setSyncStatus] = useState({});
  const [history, setHistory] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [syncing, setSyncing] = useState({});
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [newMapping, setNewMapping] = useState({ provider_group_id: '', description: '' });
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [error, setError] = useState(null);

  // EHR Configuration state
  const [ehrConfig, setEhrConfig] = useState({ ehr_type: 'ehrbridge', ehr_login_url: '', username: '', password: '' });
  const [ehrConfigLoading, setEhrConfigLoading] = useState(true);
  const [ehrConfigSaving, setEhrConfigSaving] = useState(false);
  const [ehrConfigMsg, setEhrConfigMsg] = useState(null); // { type: 'success'|'error', text }
  const [scraperTestResult, setScraperTestResult] = useState(null);
  const [testingScraper, setTestingScraper] = useState(false);

  // Initial Data Sync (Booked Appointments) state
  const [providers, setProviders] = useState([]);
  const [bulkSync, setBulkSync] = useState({ startDate: '', endDate: '', providerIds: [], includeCancelled: true, includeInsuranceDetails: false });
  const [bulkSyncRunning, setBulkSyncRunning] = useState(false);
  const [bulkSyncResult, setBulkSyncResult] = useState(null);
  const [bulkSyncProgress, setBulkSyncProgress] = useState(null);
  const [bulkSyncHistory, setBulkSyncHistory] = useState([]);

  const resources = [
    { key: 'department', label: 'Departments', endpoint: 'departments' },
    { key: 'provider', label: 'Providers', endpoint: 'providers' },
    { key: 'patient', label: 'Patients', endpoint: 'patients' },
    { key: 'appointment', label: 'Appointments', endpoint: 'appointments' },
    { key: 'appointment_type', label: 'Appointment Types', endpoint: 'appointment-types' },
    { key: 'reason_code', label: 'Reason Codes', endpoint: 'reason-codes' },
  ];

  useEffect(() => {
    loadEhrConfig();
    loadSyncStatus();
    loadHistory();
    loadMappings();
    loadAppointmentTypes();
    loadReasonCodes();
    loadProviders();
    loadBulkSyncHistory();
  }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── EHR Configuration ──────────────────────────────────

  const loadEhrConfig = async () => {
    try {
      const res = await api.get(`/practices/${practiceId}/ehr/config`);
      if (res.success && res.data) {
        setEhrConfig({
          ehr_type: res.data.ehr_type || 'ehrbridge',
          ehr_login_url: res.data.ehr_login_url || '',
          username: res.data.credential_username || '',
          password: ''
        });
      }
    } catch (err) { console.error('Load EHR config error:', err); }
    finally { setEhrConfigLoading(false); }
  };

  const saveEhrConfig = async () => {
    setEhrConfigSaving(true);
    setEhrConfigMsg(null);
    setScraperTestResult(null);
    try {
      const payload = { ehr_type: ehrConfig.ehr_type };
      if (ehrConfig.ehr_type !== 'ehrbridge') {
        payload.ehr_login_url = ehrConfig.ehr_login_url;
        payload.username = ehrConfig.username;
        if (ehrConfig.password) payload.password = ehrConfig.password;
      }
      const res = await api.put(`/practices/${practiceId}/ehr/config`, payload);
      if (res.success) {
        setEhrConfigMsg({ type: 'success', text: 'EHR configuration saved.' });
        setEhrConfig(prev => ({ ...prev, password: '' }));
        // Reload config from server to confirm persistence
        await loadEhrConfig();
      }
    } catch (err) {
      setEhrConfigMsg({ type: 'error', text: err.message || 'Failed to save EHR config' });
    } finally {
      setEhrConfigSaving(false);
    }
  };

  const testScraperConnection = async () => {
    setTestingScraper(true);
    setScraperTestResult(null);
    try {
      const res = await api.post(`/practices/${practiceId}/ehr/test-scraper`);
      setScraperTestResult({ success: true, data: res.data });
    } catch (err) {
      setScraperTestResult({ success: false, error: err.message || 'Connection test failed' });
    } finally {
      setTestingScraper(false);
    }
  };

  const isScraperType = ehrConfig.ehr_type === 'athena_scraper' || ehrConfig.ehr_type === 'ecw_scraper';

  const loadSyncStatus = async () => {
    try {
      const res = await api.get(`/practices/${practiceId}/ehr/sync/status`);
      if (res.success) setSyncStatus(res.data || {});
    } catch (err) { console.error('Load sync status error:', err); }
  };

  const loadHistory = async () => {
    try {
      const res = await api.get(`/practices/${practiceId}/ehr/sync/history?limit=20`);
      if (res.success) setHistory(res.data || []);
    } catch (err) { console.error('Load sync history error:', err); }
  };

  const loadMappings = async () => {
    try {
      const res = await api.get(`/practices/${practiceId}/ehr/provider-group-mappings`);
      if (res.success) setMappings(res.data || []);
    } catch (err) { console.error('Load mappings error:', err); }
  };

  const loadAppointmentTypes = async () => {
    try {
      const res = await api.get(`/practices/${practiceId}/ehr/appointment-types`);
      if (res.success) setAppointmentTypes(res.data || []);
    } catch (err) { console.error('Load appointment types error:', err); }
  };

  const loadReasonCodes = async () => {
    try {
      const res = await api.get(`/practices/${practiceId}/ehr/reason-codes`);
      if (res.success) setReasonCodes(res.data || []);
    } catch (err) { console.error('Load reason codes error:', err); }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const res = await api.post(`/practices/${practiceId}/ehr/test-connection`);
      setConnectionStatus(res.data);
    } catch (err) {
      setConnectionStatus({ connected: false, error: err.message });
    } finally {
      setTestingConnection(false);
    }
  };

  const syncResource = async (endpoint) => {
    setSyncing(prev => ({ ...prev, [endpoint]: true }));
    setError(null);
    try {
      await api.post(`/practices/${practiceId}/ehr/sync/${endpoint}`);
      await loadSyncStatus();
      await loadHistory();
      if (endpoint === 'appointment-types') await loadAppointmentTypes();
      if (endpoint === 'reason-codes') await loadReasonCodes();
    } catch (err) {
      setError(err.message || `Failed to sync ${endpoint}`);
    } finally {
      setSyncing(prev => ({ ...prev, [endpoint]: false }));
    }
  };

  const syncAll = async () => {
    setSyncing(prev => ({ ...prev, all: true }));
    setError(null);
    try {
      const res = await api.post(`/practices/${practiceId}/ehr/sync`);
      // syncAll catches per-resource errors so one failure doesn't tank the
      // whole run — but that means the response is "success: true" even when
      // a resource (e.g., appointment_types) silently failed. Surface those
      // partial failures here so the user can see which resource didn't sync
      // and why (e.g., Bridge 404 from a misconfigured nh_practice_id).
      const results = res?.data || {};
      const summary = results._summary;
      if (summary && summary.failed_count > 0) {
        const lines = summary.failed_resources.map(r => {
          const err = results[r]?.error || 'unknown error';
          return `• ${r}: ${err}`;
        });
        setError(`Sync completed with ${summary.failed_count} failure(s):\n${lines.join('\n')}`);
      }
      await loadSyncStatus();
      await loadHistory();
      await loadAppointmentTypes();
      await loadReasonCodes();
    } catch (err) {
      setError(err.message || 'Failed to sync all');
    } finally {
      setSyncing(prev => ({ ...prev, all: false }));
    }
  };

  const addMapping = async (e) => {
    e.preventDefault();
    if (!newMapping.provider_group_id.trim()) return;
    try {
      await api.post(`/practices/${practiceId}/ehr/provider-group-mappings`, newMapping);
      setNewMapping({ provider_group_id: '', description: '' });
      await loadMappings();
    } catch (err) {
      setError(err.message || 'Failed to add mapping');
    }
  };

  const deleteMapping = async (id) => {
    try {
      await api.delete(`/practices/${practiceId}/ehr/provider-group-mappings/${id}`);
      await loadMappings();
    } catch (err) {
      setError(err.message || 'Failed to delete mapping');
    }
  };

  const toggleAppointmentType = async (apptType) => {
    try {
      await api.put(`/practices/${practiceId}/ehr/appointment-types/${apptType.id}`, {
        enabled: !apptType.enabled
      });
      await loadAppointmentTypes();
    } catch (err) {
      setError(err.message || 'Failed to update appointment type');
    }
  };

  const toggleReasonCode = async (rc) => {
    try {
      await api.put(`/practices/${practiceId}/ehr/reason-codes/${rc.id}`, {
        enabled: !rc.enabled
      });
      await loadReasonCodes();
    } catch (err) {
      setError(err.message || 'Failed to update reason code');
    }
  };


  // ─── Initial Data Sync (Booked) ─────────────────────────

  const loadProviders = async () => {
    try {
      const res = await api.get(`/practices/${practiceId}/voice/providers`);
      if (res.success) setProviders((res.data || []).filter(p => p.athena_provider_id));
    } catch (err) { console.error('Load providers error:', err); }
  };

  const loadBulkSyncHistory = async () => {
    try {
      const res = await api.get(`/practices/${practiceId}/ehr/sync/booked/history?limit=10`);
      if (res.success) setBulkSyncHistory(res.data || []);
    } catch (err) { console.error('Load bulk sync history error:', err); }
  };

  const runBulkSync = async (e) => {
    e.preventDefault();
    if (!bulkSync.startDate || !bulkSync.endDate || !bulkSync.providerIds.length) {
      setError('Please select at least one provider, a start date, and an end date');
      return;
    }
    setBulkSyncRunning(true);
    setBulkSyncResult(null);
    setBulkSyncProgress({ done: 0, total: bulkSync.providerIds.length, failures: [] });
    setError(null);

    // Aggregate counters across provider runs
    const agg = {
      totalVisitsFetched: 0,
      patients: { created: 0, updated: 0, failed: 0 },
      appointments: { created: 0, updated: 0, failed: 0 },
      insurances: { synced: 0 }
    };
    const failures = [];

    try {
      for (let i = 0; i < bulkSync.providerIds.length; i++) {
        const pid = bulkSync.providerIds[i];
        const provider = providers.find(p => String(p.id) === String(pid));
        try {
          const res = await api.post(`/practices/${practiceId}/ehr/sync/booked`, {
            startDate: bulkSync.startDate,
            endDate: bulkSync.endDate,
            providerId: pid,
            includeCancelled: bulkSync.includeCancelled,
            includeInsuranceDetails: bulkSync.includeInsuranceDetails
          });
          if (res.success && res.data) {
            agg.totalVisitsFetched += res.data.totalVisitsFetched || 0;
            agg.patients.created += res.data.patients?.created || 0;
            agg.patients.updated += res.data.patients?.updated || 0;
            agg.patients.failed += res.data.patients?.failed || 0;
            agg.appointments.created += res.data.appointments?.created || 0;
            agg.appointments.updated += res.data.appointments?.updated || 0;
            agg.appointments.failed += res.data.appointments?.failed || 0;
            agg.insurances.synced += res.data.insurances?.synced || 0;
          }
        } catch (perProviderErr) {
          failures.push({
            providerId: pid,
            providerName: provider ? `${provider.first_name} ${provider.last_name}` : `Provider ${pid}`,
            error: perProviderErr.message || 'Sync failed'
          });
        }
        setBulkSyncProgress({ done: i + 1, total: bulkSync.providerIds.length, failures: [...failures] });
      }

      setBulkSyncResult(agg);
      if (failures.length) {
        setError(`${failures.length} of ${bulkSync.providerIds.length} providers failed: ${failures.map(f => f.providerName).join(', ')}`);
      }
      await loadBulkSyncHistory();
      await loadSyncStatus();
    } catch (err) {
      setError(err.message || 'Bulk sync failed');
    } finally {
      setBulkSyncRunning(false);
    }
  };

  const toggleProvider = (id) => {
    setBulkSync(prev => {
      const exists = prev.providerIds.some(x => String(x) === String(id));
      return {
        ...prev,
        providerIds: exists
          ? prev.providerIds.filter(x => String(x) !== String(id))
          : [...prev.providerIds, id]
      };
    });
  };

  const toggleAllProviders = () => {
    setBulkSync(prev => ({
      ...prev,
      providerIds: prev.providerIds.length === providers.length ? [] : providers.map(p => p.id)
    }));
  };

  const getStatusBadge = (status) => {
    const colors = {
      completed: { bg: '#f0fdf4', color: '#166534' },
      partial: { bg: '#fffbeb', color: '#92400e' },
      failed: { bg: '#fef2f2', color: '#dc2626' },
      running: { bg: '#eff6ff', color: '#1d4ed8' },
    };
    const c = colors[status] || colors.running;
    return {
      display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
      fontSize: '12px', fontWeight: '500', background: c.bg, color: c.color
    };
  };

  const formatTime = (ts) => {
    if (!ts) return 'Never';
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  return (
    <div>
      {error && <div style={styles.errorMsg}>{error}</div>}

      {/* EHR Configuration */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>EHR Configuration</h3>

        {ehrConfigLoading ? (
          <div style={{ color: '#6b7280', fontSize: '14px' }}>Loading configuration...</div>
        ) : (
          <>
            <div style={styles.formGroup}>
              <label style={styles.label}>EHR Adapter</label>
              <select
                style={{ ...styles.input, maxWidth: '320px' }}
                value={ehrConfig.ehr_type}
                onChange={e => {
                  setEhrConfig(prev => ({ ...prev, ehr_type: e.target.value }));
                  setEhrConfigMsg(null);
                  setScraperTestResult(null);
                }}
              >
                <option value="ehrbridge">EHRBridge API (Default)</option>
                <option value="athena_scraper">Athena Scraper</option>
                <option value="ecw_scraper">eClinicalWorks Scraper</option>
              </select>
              <span style={styles.helpText}>
                {ehrConfig.ehr_type === 'ehrbridge'
                  ? 'Uses EHRBridge API with NH Practice ID configured in General tab.'
                  : 'Connects directly to the EHR web portal using service account credentials.'}
              </span>
            </div>

            {isScraperType && (
              <div style={{ marginTop: '16px', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                  Service Account Credentials
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Login URL</label>
                  <input
                    style={styles.input}
                    type="url"
                    value={ehrConfig.ehr_login_url}
                    onChange={e => setEhrConfig(prev => ({ ...prev, ehr_login_url: e.target.value }))}
                    placeholder={ehrConfig.ehr_type === 'athena_scraper'
                      ? 'https://athenanet.athenahealth.com'
                      : 'https://yourpractice.eclinicalweb.com'}
                  />
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Username</label>
                    <input
                      style={styles.input}
                      type="text"
                      value={ehrConfig.username}
                      onChange={e => setEhrConfig(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="Service account username"
                      autoComplete="off"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Password</label>
                    <input
                      style={styles.input}
                      type="password"
                      value={ehrConfig.password}
                      onChange={e => setEhrConfig(prev => ({ ...prev, password: e.target.value }))}
                      placeholder={ehrConfig.username ? '(unchanged — enter to update)' : 'Service account password'}
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '4px' }}>
                  <button
                    onClick={saveEhrConfig}
                    disabled={ehrConfigSaving || !ehrConfig.ehr_login_url || !ehrConfig.username}
                    style={styles.saveButton}
                  >
                    {ehrConfigSaving ? 'Saving...' : 'Save Config'}
                  </button>
                  <button
                    onClick={testScraperConnection}
                    disabled={testingScraper || !ehrConfig.username}
                    style={styles.secondaryButton}
                  >
                    {testingScraper ? 'Testing...' : 'Test Connection'}
                  </button>
                </div>

                {ehrConfigMsg && (
                  <div style={{
                    marginTop: '12px', padding: '10px 14px', borderRadius: '6px', fontSize: '13px',
                    background: ehrConfigMsg.type === 'success' ? '#f0fdf4' : '#fef2f2',
                    color: ehrConfigMsg.type === 'success' ? '#166534' : '#dc2626',
                    border: `1px solid ${ehrConfigMsg.type === 'success' ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    {ehrConfigMsg.text}
                  </div>
                )}

                {scraperTestResult && (
                  <div style={{
                    marginTop: '12px', padding: '10px 14px', borderRadius: '6px', fontSize: '13px',
                    background: scraperTestResult.success ? '#f0fdf4' : '#fef2f2',
                    color: scraperTestResult.success ? '#166534' : '#dc2626',
                    border: `1px solid ${scraperTestResult.success ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    {scraperTestResult.success
                      ? `Connected successfully (${scraperTestResult.data?.adapter || ehrConfig.ehr_type})`
                      : `Connection failed: ${scraperTestResult.error}`}
                  </div>
                )}

                <div style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af' }}>
                  Create a dedicated service account in your EHR with read-only access. Credentials are encrypted at rest (AES-256-GCM).
                </div>
              </div>
            )}

            {ehrConfig.ehr_type === 'ehrbridge' && (
              <div style={{ marginTop: '8px' }}>
                <button
                  onClick={saveEhrConfig}
                  disabled={ehrConfigSaving}
                  style={styles.saveButton}
                >
                  {ehrConfigSaving ? 'Saving...' : 'Save'}
                </button>
                {ehrConfigMsg && (
                  <div style={{
                    marginTop: '10px', padding: '10px 14px', borderRadius: '6px', fontSize: '13px',
                    background: ehrConfigMsg.type === 'success' ? '#f0fdf4' : '#fef2f2',
                    color: ehrConfigMsg.type === 'success' ? '#166534' : '#dc2626',
                    border: `1px solid ${ehrConfigMsg.type === 'success' ? '#bbf7d0' : '#fecaca'}`
                  }}>
                    {ehrConfigMsg.text}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Connection & Provider Group Mappings — EHRBridge only */}
      {!isScraperType && (
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>EHRBridge Connection</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <button onClick={testConnection} disabled={testingConnection} style={styles.secondaryButton}>
            {testingConnection ? 'Testing...' : 'Test Connection'}
          </button>
          {connectionStatus && (
            <span style={{
              padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '500',
              background: connectionStatus.connected ? '#f0fdf4' : '#fef2f2',
              color: connectionStatus.connected ? '#166534' : '#dc2626'
            }}>
              {connectionStatus.connected
                ? `Connected (${connectionStatus.recordCount} departments found)`
                : `Failed: ${connectionStatus.error}`}
            </span>
          )}
        </div>

        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '16px 0 8px' }}>Provider Group Mappings</h4>
        <p style={styles.helpText}>Map EHRBridge ProviderGroupIDs to this practice. If no mappings are configured, all records will be synced.</p>

        {mappings.length > 0 && (
          <table style={ehrStyles.table}>
            <thead>
              <tr>
                <th style={ehrStyles.th}>Provider Group ID</th>
                <th style={ehrStyles.th}>Description</th>
                <th style={ehrStyles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map(m => (
                <tr key={m.id}>
                  <td style={ehrStyles.td}>{m.provider_group_id}</td>
                  <td style={ehrStyles.td}>{m.description || '-'}</td>
                  <td style={ehrStyles.td}>
                    <button onClick={() => deleteMapping(m.id)} style={ehrStyles.deleteBtn}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <form onSubmit={addMapping} style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Provider Group ID</label>
            <input style={styles.input} type="text" value={newMapping.provider_group_id}
              onChange={e => setNewMapping(prev => ({ ...prev, provider_group_id: e.target.value }))}
              placeholder="e.g. 1" />
          </div>
          <div style={{ flex: 2 }}>
            <label style={styles.label}>Description</label>
            <input style={styles.input} type="text" value={newMapping.description}
              onChange={e => setNewMapping(prev => ({ ...prev, description: e.target.value }))}
              placeholder="e.g. Main Practice Group" />
          </div>
          <button type="submit" style={{ ...styles.saveButton, whiteSpace: 'nowrap' }}>Add</button>
        </form>
      </div>
      )}

      {/* Voice Tool Routing */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Voice Tool Routing</h3>
        <p style={styles.helpText}>
          Voice AI tool calls (validate caller, get slots, book appointments, etc.) are handled locally using synced EHR data + EHRBridge API.
        </p>
        <div style={{
          padding: '14px 16px', borderRadius: '8px', marginTop: '12px',
          border: '2px solid #059669', background: '#ecfdf5'
        }}>
          <div style={{ fontWeight: '600', fontSize: '14px', color: '#059669', marginBottom: '4px' }}>
            Local DB + EHRBridge
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            Read operations (validate caller, get slots) use local synced data. Write operations (book, cancel, reschedule) go through EHRBridge API.
          </div>
        </div>
        {!selectedPractice?.nh_practice_id && (
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#dc2626' }}>
            Set NH Practice ID in General settings to enable EHRBridge integration.
          </div>
        )}
      </div>

      {/* Sync Actions */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ ...styles.sectionTitle, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Sync Resources</h3>
          <button onClick={syncAll} disabled={syncing.all} style={styles.saveButton}>
            {syncing.all ? 'Syncing All...' : 'Sync All'}
          </button>
        </div>

        <div style={ehrStyles.cardGrid}>
          {resources.map(r => {
            const status = syncStatus[r.key];
            return (
              <div key={r.key} style={ehrStyles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <strong style={{ fontSize: '14px', color: '#111827' }}>{r.label}</strong>
                  {status && <span style={getStatusBadge(status.status)}>{status.status}</span>}
                </div>
                {status ? (
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                    <div>Last sync: {formatTime(status.started_at)}</div>
                    <div>Created: {status.records_created || 0} | Updated: {status.records_updated || 0} | Failed: {status.records_failed || 0}</div>
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Never synced</div>
                )}
                <button
                  onClick={() => syncResource(r.endpoint)}
                  disabled={syncing[r.endpoint] || syncing.all}
                  style={ehrStyles.syncBtn}
                >
                  {syncing[r.endpoint] ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Initial Data Sync */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Initial Data Sync</h3>
        <p style={styles.helpText}>
          Bulk load booked appointments and patient data from EHR for a specific provider and date range.
          Existing records will be updated. Use this for initial onboarding or ad-hoc re-sync.
        </p>

        <form onSubmit={runBulkSync} style={{ marginTop: '16px' }}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Providers
                {providers.length > 0 && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280', fontWeight: '400' }}>
                    ({bulkSync.providerIds.length} of {providers.length} selected)
                  </span>
                )}
              </label>
              {providers.length === 0 ? (
                <div style={styles.helpText}>No providers with Athena IDs found. Sync providers from EHR first.</div>
              ) : (
                <div style={{
                  border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff',
                  maxHeight: '220px', overflowY: 'auto', padding: '8px'
                }}>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 4px',
                    borderBottom: '1px solid #e5e7eb', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151'
                  }}>
                    <input
                      type="checkbox"
                      checked={bulkSync.providerIds.length === providers.length && providers.length > 0}
                      onChange={toggleAllProviders}
                      style={styles.checkbox}
                    />
                    Select All
                  </label>
                  {providers.map(p => (
                    <label key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 4px',
                      cursor: 'pointer', fontSize: '13px', color: '#374151'
                    }}>
                      <input
                        type="checkbox"
                        checked={bulkSync.providerIds.some(x => String(x) === String(p.id))}
                        onChange={() => toggleProvider(p.id)}
                        style={styles.checkbox}
                      />
                      {p.first_name} {p.last_name} <span style={{ color: '#9ca3af', fontSize: '12px' }}>(Athena ID: {p.athena_provider_id})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Start Date</label>
              <input
                type="date"
                style={styles.input}
                value={bulkSync.startDate}
                onChange={e => setBulkSync(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>End Date</label>
              <input
                type="date"
                style={styles.input}
                value={bulkSync.endDate}
                onChange={e => setBulkSync(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={bulkSync.includeCancelled}
                onChange={e => setBulkSync(prev => ({ ...prev, includeCancelled: e.target.checked }))}
                style={styles.checkbox}
              />
              Include Cancelled
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151', cursor: 'pointer' }}
              title="Fetches full insurance plan details per patient from EHR. Slower but captures plan name, member ID, group number, etc."
            >
              <input
                type="checkbox"
                checked={bulkSync.includeInsuranceDetails}
                onChange={e => setBulkSync(prev => ({ ...prev, includeInsuranceDetails: e.target.checked }))}
                style={styles.checkbox}
              />
              Sync Insurance Details
              <span style={{ fontSize: '11px', color: '#9ca3af' }}>(slower)</span>
            </label>
          </div>

          <button type="submit" disabled={bulkSyncRunning} style={styles.saveButton}>
            {bulkSyncRunning
              ? (bulkSyncProgress ? `Syncing ${bulkSyncProgress.done}/${bulkSyncProgress.total}...` : 'Syncing...')
              : (bulkSync.providerIds.length > 1 ? `Start Sync (${bulkSync.providerIds.length} providers)` : 'Start Sync')}
          </button>
        </form>

        {/* Results */}
        {bulkSyncResult && (
          <div style={{
            marginTop: '16px', padding: '16px', borderRadius: '8px',
            border: '1px solid #bbf7d0', background: '#f0fdf4'
          }}>
            <div style={{ fontWeight: '600', fontSize: '14px', color: '#166534', marginBottom: '12px' }}>
              Sync Complete — {bulkSyncResult.totalVisitsFetched} visits processed
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Patients</div>
                <div style={{ fontSize: '13px', color: '#374151' }}>
                  {bulkSyncResult.patients.created} created, {bulkSyncResult.patients.updated} updated
                  {bulkSyncResult.patients.failed > 0 && <span style={{ color: '#dc2626' }}>, {bulkSyncResult.patients.failed} failed</span>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Appointments</div>
                <div style={{ fontSize: '13px', color: '#374151' }}>
                  {bulkSyncResult.appointments.created} created, {bulkSyncResult.appointments.updated} updated
                  {bulkSyncResult.appointments.failed > 0 && <span style={{ color: '#dc2626' }}>, {bulkSyncResult.appointments.failed} failed</span>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Insurance</div>
                <div style={{ fontSize: '13px', color: '#374151' }}>
                  {bulkSyncResult.insurances.synced} synced
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Sync History */}
        {bulkSyncHistory.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>Sync History</h4>
            <table style={ehrStyles.table}>
              <thead>
                <tr>
                  <th style={ehrStyles.th}>Provider</th>
                  <th style={ehrStyles.th}>Date Range</th>
                  <th style={ehrStyles.th}>Status</th>
                  <th style={ehrStyles.th}>Patients</th>
                  <th style={ehrStyles.th}>Appointments</th>
                  <th style={ehrStyles.th}>Started</th>
                </tr>
              </thead>
              <tbody>
                {bulkSyncHistory.map(j => (
                  <tr key={j.id}>
                    <td style={ehrStyles.td}>{j.provider_name || '—'}</td>
                    <td style={ehrStyles.td}>{j.start_date} to {j.end_date}</td>
                    <td style={ehrStyles.td}><span style={getStatusBadge(j.status)}>{j.status}</span></td>
                    <td style={ehrStyles.td}>{(j.patients_created || 0) + (j.patients_updated || 0)}c/u, {j.patients_failed || 0}f</td>
                    <td style={ehrStyles.td}>{(j.appointments_created || 0) + (j.appointments_updated || 0)}c/u, {j.appointments_failed || 0}f</td>
                    <td style={ehrStyles.td}>{formatTime(j.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Synced Appointment Types */}
      <CollapsibleSection title={`Synced Appointment Types (${appointmentTypes.length})`} defaultOpen={false}>
        {appointmentTypes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
            No appointment types synced yet. Click "Sync Now" on Appointment Types above.
          </div>
        ) : (
          <table style={ehrStyles.table}>
            <thead>
              <tr>
                <th style={ehrStyles.th}>EHR Type ID</th>
                <th style={ehrStyles.th}>Name</th>
                <th style={ehrStyles.th}>Duration</th>
                <th style={ehrStyles.th}>Enabled</th>
                <th style={ehrStyles.th}>Last Synced</th>
              </tr>
            </thead>
            <tbody>
              {appointmentTypes.map(t => (
                <tr key={t.id}>
                  <td style={ehrStyles.td}>{t.ehr_type_id}</td>
                  <td style={ehrStyles.td}>{t.name}</td>
                  <td style={ehrStyles.td}>{t.duration_minutes ? `${t.duration_minutes} min` : '—'}</td>
                  <td style={ehrStyles.td}>
                    <button
                      onClick={() => toggleAppointmentType(t)}
                      style={{
                        padding: '3px 10px', borderRadius: '4px', border: '1px solid',
                        fontSize: '12px', cursor: 'pointer',
                        background: t.enabled ? '#f0fdf4' : '#fff',
                        color: t.enabled ? '#166534' : '#6b7280',
                        borderColor: t.enabled ? '#bbf7d0' : '#d1d5db'
                      }}
                    >
                      {t.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td style={ehrStyles.td}>{formatTime(t.last_synced_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CollapsibleSection>

      {/* Synced Reason Codes */}
      <CollapsibleSection title={`Synced Reason Codes (${reasonCodes.length})`} defaultOpen={false}>
        {reasonCodes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
            No reason codes synced yet. Click "Sync Now" on Reason Codes above.
          </div>
        ) : (
          <table style={ehrStyles.table}>
            <thead>
              <tr>
                <th style={ehrStyles.th}>EHR Reason ID</th>
                <th style={ehrStyles.th}>Reason Text</th>
                <th style={ehrStyles.th}>Enabled</th>
                <th style={ehrStyles.th}>Last Synced</th>
              </tr>
            </thead>
            <tbody>
              {reasonCodes.map(rc => (
                <tr key={rc.id}>
                  <td style={ehrStyles.td}>{rc.ehr_reason_id}</td>
                  <td style={ehrStyles.td}>{rc.reason_text}</td>
                  <td style={ehrStyles.td}>
                    <button
                      onClick={() => toggleReasonCode(rc)}
                      style={{
                        padding: '3px 10px', borderRadius: '4px', border: '1px solid',
                        fontSize: '12px', cursor: 'pointer',
                        background: rc.enabled ? '#f0fdf4' : '#fff',
                        color: rc.enabled ? '#166534' : '#6b7280',
                        borderColor: rc.enabled ? '#bbf7d0' : '#d1d5db'
                      }}
                    >
                      {rc.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td style={ehrStyles.td}>{formatTime(rc.last_synced_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CollapsibleSection>

      {/* Sync History */}
      <CollapsibleSection title={`Sync History (${history.length})`} defaultOpen={false}>
        {history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>No sync history yet</div>
        ) : (
          <table style={ehrStyles.table}>
            <thead>
              <tr>
                <th style={ehrStyles.th}>Resource</th>
                <th style={ehrStyles.th}>Status</th>
                <th style={ehrStyles.th}>Created</th>
                <th style={ehrStyles.th}>Updated</th>
                <th style={ehrStyles.th}>Failed</th>
                <th style={ehrStyles.th}>Started</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id}>
                  <td style={ehrStyles.td}>{h.resource_type}</td>
                  <td style={ehrStyles.td}><span style={getStatusBadge(h.status)}>{h.status}</span></td>
                  <td style={ehrStyles.td}>{h.records_created || 0}</td>
                  <td style={ehrStyles.td}>{h.records_updated || 0}</td>
                  <td style={ehrStyles.td}>{h.records_failed || 0}</td>
                  <td style={ehrStyles.td}>{formatTime(h.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CollapsibleSection>

    </div>
  );
}

function CollapsibleSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', border: 'none', background: open ? '#f9fafb' : '#fff',
          cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#111827',
        }}
      >
        <span>{title}</span>
        <span style={{ fontSize: 12, color: '#9ca3af', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}>▼</span>
      </button>
      {open && <div style={{ padding: '0 16px 16px' }}>{children}</div>}
    </div>
  );
}

const ehrStyles = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #e5e7eb',
    color: '#6b7280', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase'
  },
  td: { padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151' },
  cardGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '12px'
  },
  card: {
    border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px',
    background: '#fafafa'
  },
  syncBtn: {
    padding: '5px 14px', borderRadius: '4px', border: '1px solid #d1d5db',
    background: '#fff', fontSize: '12px', fontWeight: '500', cursor: 'pointer',
    color: '#374151'
  },
  deleteBtn: {
    padding: '3px 10px', borderRadius: '4px', border: '1px solid #fca5a5',
    background: '#fff', fontSize: '12px', color: '#dc2626', cursor: 'pointer'
  }
};

// ─── Chat Widget Tab ─────────────────────────────────────────
function ChatWidgetTab({ practiceId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    welcome_message: 'Hi! How can I help you today?',
    theme_color: '#4f46e5',
    position: 'bottom-right',
    enabled_capabilities: ['booking', 'faq', 'billing'],
    escalation_timeout_minutes: 3,
    office_hours_only: false,
    allowed_domains: [],
    ai_system_prompt: ''
  });

  useEffect(() => {
    loadConfig();
  }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/practices/${practiceId}/chat/config`);
      if (res.success && res.data) {
        setConfig(res.data);
        setForm({
          enabled: !!res.data.enabled,
          welcome_message: res.data.welcome_message || 'Hi! How can I help you today?',
          theme_color: res.data.theme_color || '#4f46e5',
          position: res.data.position || 'bottom-right',
          enabled_capabilities: res.data.enabled_capabilities || ['booking', 'faq', 'billing'],
          escalation_timeout_minutes: res.data.escalation_timeout_minutes || 3,
          office_hours_only: !!res.data.office_hours_only,
          allowed_domains: res.data.allowed_domains || [],
          ai_system_prompt: res.data.ai_system_prompt || ''
        });
      }
    } catch (err) {
      console.error('Load chat config error:', err);
    }
    setLoading(false);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        enabled_capabilities: JSON.stringify(form.enabled_capabilities),
        allowed_domains: form.allowed_domains.length > 0 ? JSON.stringify(form.allowed_domains) : null
      };
      const res = await api.put(`/practices/${practiceId}/chat/config`, payload);
      if (res.success) {
        setConfig(res.data);
        alert('Chat widget settings saved!');
      }
    } catch (err) {
      alert('Error saving: ' + err.message);
    }
    setSaving(false);
  };

  const regenerateKey = async () => {
    if (!window.confirm('Regenerate widget key? This will break existing embeds until you update the snippet.')) return;
    try {
      const res = await api.post(`/practices/${practiceId}/chat/config/regenerate-key`);
      if (res.success) {
        setConfig(res.data);
        alert('Widget key regenerated. Update your embed code.');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const toggleCapability = (cap) => {
    setForm(prev => {
      const caps = prev.enabled_capabilities.includes(cap)
        ? prev.enabled_capabilities.filter(c => c !== cap)
        : [...prev.enabled_capabilities, cap];
      return { ...prev, enabled_capabilities: caps };
    });
  };

  const getEmbedCode = () => {
    const key = config?.widget_key || 'YOUR_WIDGET_KEY';
    const base = window.location.origin;
    return `<script src="${base}/chat-widget.js" data-practice-id="${key}"></script>`;
  };

  const copyEmbed = () => {
    navigator.clipboard.writeText(getEmbedCode());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div style={{ padding: '20px', color: '#6b7280' }}>Loading...</div>;

  const capabilities = [
    { key: 'booking', label: 'Appointment Booking', desc: 'AI helps patients find slots and book appointments' },
    { key: 'faq', label: 'General FAQ', desc: 'Answer questions about hours, location, insurance, etc.' },
    { key: 'billing', label: 'Billing Inquiries', desc: 'Check balance and provide payment links' },
    { key: 'prescriptions', label: 'Prescription Refills', desc: 'Collect info and escalate refill requests to staff' },
    { key: 'referrals', label: 'Referral Status', desc: 'Check referral status and escalate if needed' }
  ];

  const cwStyles = {
    section: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', marginBottom: '16px' },
    sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' },
    field: { marginBottom: '14px' },
    label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' },
    input: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' },
    textarea: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', minHeight: '80px', fontFamily: 'inherit', resize: 'vertical' },
    select: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: '#fff' },
    toggle: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
    embedBox: { background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all', marginBottom: '8px' },
    capGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
    capCard: (active) => ({
      padding: '12px', border: `2px solid ${active ? '#4f46e5' : '#e5e7eb'}`, borderRadius: '8px',
      cursor: 'pointer', background: active ? '#f5f3ff' : '#fff', transition: 'all 0.15s'
    }),
    badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
    colorPreview: (color) => ({
      width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #d1d5db',
      background: color, display: 'inline-block', verticalAlign: 'middle', marginLeft: '8px', cursor: 'pointer'
    }),
    row: { display: 'flex', alignItems: 'center', gap: '12px' },
    hint: { fontSize: '12px', color: '#6b7280', marginTop: '4px' }
  };

  return (
    <div>
      {/* Enable/Disable */}
      <div style={cwStyles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={cwStyles.sectionTitle}>Chat Widget</div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              Allow website visitors to chat with your AI assistant for appointment booking, billing questions, and more.
            </div>
          </div>
          <label style={cwStyles.toggle}>
            <input type="checkbox" checked={form.enabled}
              onChange={e => setForm(prev => ({ ...prev, enabled: e.target.checked }))} />
            <span style={{ ...cwStyles.badge, background: form.enabled ? '#d1fae5' : '#fee2e2', color: form.enabled ? '#065f46' : '#991b1b' }}>
              {form.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
      </div>

      {/* Embed Code */}
      {config?.widget_key && (
        <div style={cwStyles.section}>
          <div style={cwStyles.sectionTitle}>Embed Code</div>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
            Add this snippet to your website, just before the closing <code>&lt;/body&gt;</code> tag:
          </p>
          <div style={cwStyles.embedBox}>{getEmbedCode()}</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={copyEmbed} style={{
              padding: '6px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
              background: copied ? '#d1fae5' : '#fff', fontSize: '13px', cursor: 'pointer'
            }}>
              {copied ? 'Copied!' : 'Copy Snippet'}
            </button>
            <button onClick={regenerateKey} style={{
              padding: '6px 16px', borderRadius: '6px', border: '1px solid #fca5a5',
              background: '#fff', fontSize: '13px', color: '#dc2626', cursor: 'pointer'
            }}>
              Regenerate Key
            </button>
          </div>
          <div style={cwStyles.hint}>Widget Key: {config.widget_key}</div>
        </div>
      )}

      {/* Appearance */}
      <div style={cwStyles.section}>
        <div style={cwStyles.sectionTitle}>Appearance</div>
        <div style={cwStyles.field}>
          <label style={cwStyles.label}>Welcome Message</label>
          <input style={cwStyles.input} value={form.welcome_message}
            onChange={e => setForm(prev => ({ ...prev, welcome_message: e.target.value }))} />
        </div>
        <div style={cwStyles.field}>
          <label style={cwStyles.label}>
            Theme Color
            <span style={cwStyles.colorPreview(form.theme_color)} />
          </label>
          <div style={cwStyles.row}>
            <input type="color" value={form.theme_color}
              onChange={e => setForm(prev => ({ ...prev, theme_color: e.target.value }))} />
            <input style={{ ...cwStyles.input, width: '120px' }} value={form.theme_color}
              onChange={e => setForm(prev => ({ ...prev, theme_color: e.target.value }))}
              placeholder="#4f46e5" />
          </div>
        </div>
        <div style={cwStyles.field}>
          <label style={cwStyles.label}>Position</label>
          <select style={cwStyles.select} value={form.position}
            onChange={e => setForm(prev => ({ ...prev, position: e.target.value }))}>
            <option value="bottom-right">Bottom Right</option>
            <option value="bottom-left">Bottom Left</option>
          </select>
        </div>
      </div>

      {/* Capabilities */}
      <div style={cwStyles.section}>
        <div style={cwStyles.sectionTitle}>Capabilities</div>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
          Select what the AI assistant can help with:
        </p>
        <div style={cwStyles.capGrid}>
          {capabilities.map(cap => (
            <div key={cap.key} style={cwStyles.capCard(form.enabled_capabilities.includes(cap.key))}
              onClick={() => toggleCapability(cap.key)}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{cap.label}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{cap.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Escalation & Advanced */}
      <div style={cwStyles.section}>
        <div style={cwStyles.sectionTitle}>Escalation Settings</div>
        <div style={cwStyles.field}>
          <label style={cwStyles.label}>Staff Response Timeout (minutes)</label>
          <div style={cwStyles.row}>
            <input type="range" min="1" max="10" value={form.escalation_timeout_minutes}
              onChange={e => setForm(prev => ({ ...prev, escalation_timeout_minutes: parseInt(e.target.value) }))} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>{form.escalation_timeout_minutes} min</span>
          </div>
          <div style={cwStyles.hint}>
            After escalation, if no staff responds within this time, the AI will ask for a callback number.
          </div>
        </div>
        <div style={cwStyles.field}>
          <label style={cwStyles.toggle}>
            <input type="checkbox" checked={form.office_hours_only}
              onChange={e => setForm(prev => ({ ...prev, office_hours_only: e.target.checked }))} />
            <span style={{ fontSize: '14px' }}>Only show during office hours</span>
          </label>
        </div>
      </div>

      {/* Custom AI Prompt */}
      <div style={cwStyles.section}>
        <div style={cwStyles.sectionTitle}>Custom AI Instructions (Optional)</div>
        <textarea style={cwStyles.textarea} value={form.ai_system_prompt}
          onChange={e => setForm(prev => ({ ...prev, ai_system_prompt: e.target.value }))}
          placeholder="Add any additional instructions for the AI assistant..." />
        <div style={cwStyles.hint}>
          These instructions are appended to the default system prompt. Use this for practice-specific guidelines.
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
        <button onClick={saveConfig} disabled={saving} style={{
          padding: '10px 24px', borderRadius: '6px', border: 'none',
          background: '#4f46e5', color: '#fff', fontSize: '14px',
          fontWeight: '500', cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.7 : 1
        }}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: { padding: '24px' },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  title: { fontSize: '24px', fontWeight: '600', color: '#111827', margin: 0 },
  practiceBadge: {
    background: '#ede9fe', color: '#6d28d9', padding: '4px 12px',
    borderRadius: '12px', fontSize: '13px', fontWeight: '500'
  },
  emptyState: { textAlign: 'center', padding: '40px', color: '#6b7280' },
  loading: { textAlign: 'center', padding: '40px', color: '#6b7280' },

  // Tabs
  tabBar: {
    display: 'flex', gap: '4px', marginBottom: '20px',
    borderBottom: '2px solid #e5e7eb', paddingBottom: '0'
  },
  tab: {
    padding: '10px 20px', border: 'none', background: 'transparent',
    fontSize: '14px', fontWeight: '500', color: '#6b7280',
    cursor: 'pointer', borderBottom: '2px solid transparent',
    marginBottom: '-2px', transition: 'all 0.15s ease'
  },
  tabActive: {
    color: '#4f46e5', borderBottomColor: '#4f46e5'
  },

  // Sections
  section: {
    background: '#fff', borderRadius: '8px', padding: '20px',
    marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  sectionTitle: {
    fontSize: '16px', fontWeight: '600', color: '#111827',
    marginTop: 0, marginBottom: '16px', paddingBottom: '12px',
    borderBottom: '1px solid #e5e7eb'
  },

  // Form
  formRow: { display: 'flex', gap: '16px', marginBottom: '0' },
  formGroup: { flex: 1, marginBottom: '14px' },
  label: {
    display: 'flex', alignItems: 'center', gap: '8px',
    fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px'
  },
  input: {
    width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box'
  },
  textarea: {
    width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '14px', resize: 'vertical',
    fontFamily: 'inherit', boxSizing: 'border-box'
  },
  checkbox: { width: '16px', height: '16px' },
  helpText: { fontSize: '12px', color: '#9ca3af', marginTop: '4px', display: 'block' },
  sliderRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  slider: { flex: 1 },
  sliderValue: { fontSize: '14px', fontWeight: '600', color: '#3b82f6', minWidth: '40px' },

  // Messages
  errorMsg: {
    background: '#fef2f2', color: '#dc2626', padding: '12px 16px',
    borderRadius: '8px', marginBottom: '16px', fontSize: '14px'
  },
  successMsg: {
    background: '#f0fdf4', color: '#166534', padding: '12px 16px',
    borderRadius: '8px', marginBottom: '16px', fontSize: '14px'
  },
  messageBanner: {
    padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px'
  },
  infoBox: {
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px',
    padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#475569'
  },

  // Actions
  actions: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' },
  saveButton: {
    padding: '10px 24px', borderRadius: '6px', border: 'none',
    background: '#4f46e5', color: '#fff', fontSize: '14px',
    fontWeight: '500', cursor: 'pointer'
  },
  secondaryButton: {
    padding: '10px 24px', borderRadius: '6px',
    border: '1px solid #d1d5db', background: '#fff',
    color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
  }
};

// ─── Landing Page Tab ─────────────────────────────────────────
function LandingPageTab({ practiceId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!practiceId) return;
    setLoading(true);
    practiceService.getLandingPageStatus(practiceId)
      .then(res => setStatus(res.data))
      .catch(() => setStatus({ published: false }))
      .finally(() => setLoading(false));
  }, [practiceId]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await practiceService.publishLandingPage(practiceId);
      setStatus({ published: true, url: res.data.url, publishedAt: res.data.publishedAt });
    } catch (err) {
      alert('Failed to publish: ' + (err.response?.data?.error || err.message));
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!window.confirm('Remove the landing page? The URL will stop working.')) return;
    setPublishing(true);
    try {
      await practiceService.unpublishLandingPage(practiceId);
      setStatus({ published: false, url: null, publishedAt: null });
    } catch (err) {
      alert('Failed to unpublish: ' + (err.response?.data?.error || err.message));
    } finally {
      setPublishing(false);
    }
  };

  const copyUrl = () => {
    if (status?.url) {
      navigator.clipboard.writeText(status.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const previewUrl = practiceService.getLandingPagePreviewUrl(practiceId);

  if (loading) {
    return <div style={styles.card}><p style={{ color: '#6b7280' }}>Loading...</p></div>;
  }

  return (
    <div style={styles.card}>
      <h3 style={styles.cardTitle}>Practice Landing Page</h3>
      <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
        Publish a branded landing page for your practice with an embedded AI chat widget.
        Share the URL with patients or link it from your website.
      </p>

      {status?.published ? (
        <div>
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px',
            padding: '16px', marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '14px' }}>Published</span>
              {status.publishedAt && (
                <span style={{ color: '#6b7280', fontSize: '12px' }}>
                  {new Date(status.publishedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                readOnly
                value={status.url}
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: '6px',
                  border: '1px solid #d1d5db', fontSize: '13px',
                  background: '#fff', color: '#1e293b', fontFamily: 'monospace'
                }}
              />
              <button onClick={copyUrl} style={{
                ...styles.secondaryButton, padding: '8px 16px', fontSize: '13px'
              }}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <a href={status.url} target="_blank" rel="noopener noreferrer" style={{
                ...styles.secondaryButton, padding: '8px 16px', fontSize: '13px',
                textDecoration: 'none', display: 'inline-block'
              }}>
                Open
              </a>
            </div>
          </div>

          <div style={styles.actions}>
            <button onClick={handlePublish} disabled={publishing} style={{
              ...styles.saveButton, opacity: publishing ? 0.6 : 1
            }}>
              {publishing ? 'Republishing...' : 'Republish'}
            </button>
            <button onClick={handleUnpublish} disabled={publishing} style={{
              ...styles.secondaryButton, color: '#dc2626', borderColor: '#fca5a5',
              opacity: publishing ? 0.6 : 1
            }}>
              Unpublish
            </button>
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{
              ...styles.secondaryButton, textDecoration: 'none', display: 'inline-block'
            }}>
              Preview
            </a>
          </div>
        </div>
      ) : (
        <div>
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px',
            padding: '20px', textAlign: 'center', marginBottom: '16px'
          }}>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>
              No landing page published yet.
            </p>
            <p style={{ color: '#94a3b8', fontSize: '12px' }}>
              The page will use your practice name, contact info, branding color, logo, and chat widget.
            </p>
          </div>
          <div style={styles.actions}>
            <button onClick={handlePublish} disabled={publishing} style={{
              ...styles.saveButton, opacity: publishing ? 0.6 : 1
            }}>
              {publishing ? 'Publishing...' : 'Publish Landing Page'}
            </button>
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{
              ...styles.secondaryButton, textDecoration: 'none', display: 'inline-block'
            }}>
              Preview
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default PracticeSettings;
